<template>
  <el-dialog
    :visible="true"
    :close-on-click-modal="false"
    :modal-append-to-body="false"
    :before-close="onClose"
    width="1200px"
    :title="
      date
        ? $t('Audiences_SegmentEvaluations_MembersPreview_DialogTitle', {
            date: date
          })
        : $t('Audiences_Segment_DialogMembersList_Title')
    "
    center
  >
    <rest-list
      v-if="columnsReady"
      :payload="fetchEndpoint"
      :filterable="false"
      ref="rest-list"
    >
      <rest-list-column
        v-if="selectedColumns.hasOwnProperty('id')"
        prop="id"
        :title="$t('UserProfiles_Field_Id')"
        :route="rowClickRoute"
        :new-tab="true"
        :min-width="280"
        :sortable="false"
      />

      <rest-list-column-enum
        v-if="selectedColumns.identifier"
        prop="identifier"
        :enum="profileIdentifiersLabels"
        :title="$t('UserProfiles_Field_Identifier')"
        :min-width="120"
        :sortable="false"
      />

      <rest-list-column
        v-if="selectedColumns.user_id"
        prop="user_id"
        :title="$t('UserProfiles_Field_UserId')"
        :min-width="180"
        :sortable="false"
      />

      <rest-list-column
        v-if="
          selectedColumns.customer_id &&
            $store.state.app.features.customerIdEnabled
        "
        prop="customer_id"
        :title="$t('UserProfiles_Field_CustomerId')"
        :min-width="180"
        :sortable="false"
      />

      <rest-list-column
        v-if="selectedColumns.email"
        prop="email"
        :title="$t('UserProfiles_Field_Email')"
        :min-width="180"
        :sortable="false"
      />

      <rest-list-column
        v-if="selectedColumns.mobile_number"
        prop="mobile_number"
        :title="$t('UserProfiles_Field_MobileNumber')"
        :min-width="180"
        :sortable="false"
      />

      <rest-list-column
        v-if="selectedColumns.whatsapp_number"
        prop="whatsapp_number"
        :title="$t('UserProfiles_Field_WhatsappNumber')"
        :min-width="180"
        :sortable="false"
      />

      <rest-list-column-boolean
        v-if="selectedColumns.email_addressable"
        prop="email_addressable"
        :title="$t('UserProfiles_Field_EmailAddressable')"
        :min-width="140"
        :sortable="false"
      />

      <rest-list-column-boolean
        v-if="selectedColumns.email_subscription"
        prop="email_subscription"
        :title="$t('UserProfiles_Field_EmailSubscription')"
        :min-width="140"
        :sortable="false"
      />

      <rest-list-column-boolean
        v-if="selectedColumns.sms_addressable"
        prop="sms_addressable"
        :title="$t('UserProfiles_Field_SmsAddressable')"
        :min-width="140"
        :sortable="false"
      />

      <rest-list-column-boolean
        v-if="selectedColumns.sms_subscription"
        prop="sms_subscription"
        :title="$t('UserProfiles_Field_SmsSubscription')"
        :min-width="140"
        :sortable="false"
      />

      <rest-list-column-boolean
        v-if="selectedColumns.whatsapp_addressable"
        prop="whatsapp_addressable"
        :title="$t('UserProfiles_Field_WhatsappAddressable')"
        :min-width="140"
        :sortable="false"
      />

      <rest-list-column-boolean
        v-if="selectedColumns.whatsapp_subscription"
        prop="whatsapp_subscription"
        :title="$t('UserProfiles_Field_WhatsappSubscription')"
        :min-width="140"
        :sortable="false"
      />

      <div v-for="(extraColumn, index) in extraColumns" :key="index">
        <rest-list-column
          v-if="selectedColumns[index]"
          :prop="index"
          :title="extraColumn.title"
          :min-width="140"
          :sortable="false"
        />
      </div>

      <el-table-column :width="50" align="center">
        <template slot="header" slot-scope="{}">
          <rest-list-column-settings
            storage-key="preview-profiles-list-columns"
            :title="$t('Profiles_Field_Settings')"
            :columns="searchColumns"
            v-model="selectedColumns"
            @updated="refreshTable"
            :dynamic-columns="true"
            @update-columns="updateColumns"
            :group-columns="true"
          />
        </template>
      </el-table-column>
    </rest-list>
  </el-dialog>
</template>

<script>
import { projectEndpoint, projectRoute } from '@/core/helpers'
import RestList from '@/common/components/rest/RestList'
import RestListColumn from '@/common/components/rest/RestListColumn'
import RestListColumnBoolean from '@/common/components/rest/RestListColumnBoolean'
import { EventBus } from '@/core/EventBus'
import { profileIdentifiersLabels } from '@/modules/profiles/enum/ProfileIdentifiersEnum'
import RestListColumnSettings from '@/common/components/rest/RestListColumnSettings'
import RestListColumnEnum from '@/common/components/rest/RestListColumnEnum'
import RestApi from '@/core/services/RestApi'
export default {
  name: 'MembersPreviewListDialog',

  props: {
    profileIds: {
      type: Array,
      required: true
    },
    date: {
      type: String,
      required: false
    }
  },

  components: {
    RestListColumnEnum,
    RestListColumnSettings,
    RestListColumnBoolean,
    RestListColumn,
    RestList
  },

  data() {
    return {
      selectedColumns: {},
      customAttributes: [],
      computedAttributes: [],
      profileIdentifiersLabels,
      columnsReady: false,
      filteredColumns: []
    }
  },

  mounted() {
    Promise.all([
      RestApi({
        ...this.fetchAttributesEndpoint
      }),
      RestApi({
        ...this.fetchComputedAttributesEndpoint
      })
    ]).then(values => {
      this.customAttributes = values[0].data
      this.computedAttributes = values[1].data
      this.columnsReady = true
    })
  },

  methods: {
    onClose() {
      EventBus.$emit('members-preview-list-close')
    },

    rowClickRoute(row) {
      return projectRoute({
        name: 'ProfileOverview',
        query: {
          profile_id: row.id
        }
      })
    },

    refreshTable() {
      this.$refs['rest-list'].refresh()
    },

    updateColumns(inputValue) {
      const attributesEndpoint = {
        ...this.fetchAttributesEndpoint,
        params:
          inputValue !== null && inputValue !== ''
            ? { title: inputValue, attribute_profile: 1, sort: 'title' }
            : { attribute_profile: 1, sort: 'title' }
      }

      const computedAttributesEndpoint = {
        ...this.fetchComputedAttributesEndpoint,
        params:
          inputValue !== null && inputValue !== ''
            ? { title: inputValue, aggregate: 1, sort: 'title' }
            : { aggregate: 1, sort: 'title' }
      }

      Promise.all([
        RestApi(attributesEndpoint),
        RestApi(computedAttributesEndpoint)
      ]).then(values => {
        this.customAttributes = values[0].data
        this.computedAttributes = values[1].data
        if (inputValue !== null && inputValue !== '') {
          this.filteredColumns = Object.keys(this.columns)
            .map(key => {
              return {
                title: this.columns[key].title,
                key: key
              }
            })
            .filter(col => {
              return col.title.toLowerCase().includes(inputValue.toLowerCase())
            })
        } else {
          this.filteredColumns = []
        }
      })
    }
  },

  computed: {
    fetchEndpoint() {
      return projectEndpoint({
        url: `user-profiles`,
        params: {
          id: this.profileIds.join(),
          expand: this.expand
        }
      })
    },

    fetchAttributesEndpoint() {
      return projectEndpoint({
        method: 'get',
        url: `tags`,
        params: { attribute_profile: 1, sort: 'title' }
      })
    },

    fetchComputedAttributesEndpoint() {
      return projectEndpoint({
        method: 'get',
        url: `tags`,
        params: { aggregate: 1, sort: 'title' }
      })
    },

    features() {
      return this.$store.state.app.features
    },

    columns() {
      return {
        id: {
          title: this.$t('UserProfiles_Field_Id'),
          defaultValue: true,
          validation: true
        },
        identifier: {
          title: this.$t('UserProfiles_Field_Identifier'),
          defaultValue: false,
          validation: true
        },
        user_id: {
          title: this.$t('UserProfiles_Field_UserId'),
          defaultValue: true,
          validation: this.$can('UserPersonalData::Read')
        },
        customer_id: {
          title: this.$t('UserProfiles_Field_CustomerId'),
          defaultValue: false,
          validation:
            this.$can('UserPersonalData::Read') &&
            this.$store.state.app.features.customerIdEnabled
        },
        email: {
          title: this.$t('UserProfiles_Field_Email'),
          defaultValue: false,
          validation: this.$can('UserPersonalData::Read')
        },
        mobile_number: {
          title: this.$t('UserProfiles_Field_MobileNumber'),
          defaultValue: false,
          validation: this.$can('UserPersonalData::Read')
        },
        whatsapp_number: {
          title: this.$t('UserProfiles_Field_WhatsappNumber'),
          defaultValue: false,
          validation:
            this.features.whatsapp && this.$can('UserPersonalData::Read')
        },
        email_addressable: {
          title: this.$t('UserProfiles_Field_EmailAddressable'),
          defaultValue: false,
          validation: true
        },
        email_subscription: {
          title: this.$t('UserProfiles_Field_EmailSubscription'),
          defaultValue: false,
          validation: true
        },
        sms_addressable: {
          title: this.$t('UserProfiles_Field_SmsAddressable'),
          defaultValue: false,
          validation: true
        },
        sms_subscription: {
          title: this.$t('UserProfiles_Field_SmsSubscription'),
          defaultValue: false,
          validation: true
        },
        whatsapp_addressable: {
          title: this.$t('UserProfiles_Field_WhatsappAddressable'),
          defaultValue: false,
          validation: this.features.whatsapp
        },
        whatsapp_subscription: {
          title: this.$t('UserProfiles_Field_WhatsappSubscription'),
          defaultValue: false,
          validation: this.features.whatsapp
        },
        ...this.extraColumns
      }
    },

    extraColumns() {
      return {
        ...this.customAttributes.reduce((result, item) => {
          result[item.name] = {
            title: item.name,
            defaultValue: false,
            validation: true,
            group: 'custom'
          }
          return result
        }, {}),
        ...this.computedAttributes.reduce((result, item) => {
          result[item.name] = {
            title: item.name,
            defaultValue: false,
            validation: true,
            group: 'computed'
          }
          return result
        }, {})
      }
    },

    searchColumns() {
      if (this.filteredColumns.length > 0) {
        return Object.keys(this.columns)
          .filter(key => this.filteredColumns.map(e => e.key).includes(key))
          .reduce((obj, key) => {
            obj[key] = this.columns[key]
            return obj
          }, {})
      } else {
        return this.columns
      }
    },

    expand() {
      let expands = this.expandsColumns.reduce((accumulator, currentValue) => {
        return accumulator + '],attributes[' + currentValue
      }, [])

      if (this.expandsColumns.length > 0) {
        return `attributes[${expands}]`.replace('attributes[],', '')
      }
    },

    expandsColumns() {
      return Object.keys(this.selectedColumns).filter(element => {
        return (
          Object.keys(this.extraColumns).includes(element) &&
          this.selectedColumns[element]
        )
      })
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/modules/audiences/components/MembersPreviewListDialog.vue